import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  styled,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  getProjectScopes,
  getScopeAnalytics,
} from "../../../../services/scopeService";
import AddScopeModal from "./AddScopeModal";
import DeleteScopeModal from "./DeleteScopeModal";
import ScopeAnalytics from "./ScopeAnalytics";
import ScopeList from "./ScopeList";
import styles from "./styles";

const StyledButton = styled(Button)(() => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "none",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: 800,
  borderRadius: "10px",
  padding: ".5vh .7vw",
  letterSpacing: 0,
}));

const scopeTabs = [
  { label: "All", status: null },
  { label: "Created", status: "CREATED" },
  { label: "In Bid", status: "BID" },
  { label: "Contracted", status: "CONTRACTED" },
];

const Scopes = ({ details, isMobile, passedStyles }) => {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [isAddScopeModalOpen, setIsAddScopeModalOpen] = useState(false);
  const [editingScope, setEditingScope] = useState(null);
  const [scopeToDelete, setScopeToDelete] = useState(null);
  const [isDeleteScopeModalOpen, setIsDeleteScopeModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("All");

  const scopeStyles = styles(isMobile);
  const statusFilter = scopeTabs.find((t) => t.label === activeTab)?.status ?? null;

  useEffect(() => {
    if (!details?.id) return;
    loadScopes();
    loadAnalytics();
  }, [details?.id, statusFilter]);

  const loadScopes = async () => {
    try {
      setLoading(true);
      const response = await getProjectScopes(details.id, statusFilter);
      if (response?.success) {
        setScopes(response.scopes || []);
      } else {
        toast.error("Failed to load scopes");
      }
    } catch (error) {
      console.error("Error loading scopes:", error);
      toast.error("Failed to load scopes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await getScopeAnalytics(details.id, statusFilter);
      if (response?.success) {
        setAnalytics(response.data || null);
      }
    } catch (error) {
      console.error("Error loading scope analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleAddScope = () => {
    setEditingScope(null);
    setIsAddScopeModalOpen(true);
  };

  const handleEditScope = (scope) => {
    setEditingScope(scope);
    setIsAddScopeModalOpen(true);
  };

  const handleDeleteScope = (scope) => {
    setScopeToDelete(scope);
    setIsDeleteScopeModalOpen(true);
  };

  const handleScopeSave = () => {
    setIsAddScopeModalOpen(false);
    setEditingScope(null);
    loadScopes();
    loadAnalytics();
  };

  const handleDeleteSuccess = () => {
    setScopeToDelete(null);
    loadScopes();
    loadAnalytics();
  };

  return (
    <div style={scopeStyles.container}>
      {/* Header */}
      <Box sx={scopeStyles.header}>
        <Box>
          <div style={scopeStyles.title}>Scopes & Quotations</div>
        </Box>

        {/* Filter tabs + Add Scope CTA hidden — Scopes tab is read-only */}
      </Box>

      {/* Analytics hidden — Scopes tab is read-only */}

      {/* Scopes List */}
      {loading ? (
        <Box sx={scopeStyles.loadingContainer}>
          <CircularProgress size={40} />
        </Box>
      ) : scopes.length === 0 ? (
        <Box sx={scopeStyles.emptyState}>
          <center
            style={{
              padding: "1vh 2vmax",
              fontSize: "16px",
              fontWeight: 500,
              color: "#0d0d0e",
              letterSpacing: 0,
            }}
          >
            No scopes found
          </center>
          <Typography variant="body1" sx={scopeStyles.emptyStateSubtitle}>
            Create your first scope to get started
          </Typography>
        </Box>
      ) : (
        <ScopeList
          scopes={scopes}
          handleManageEdit={handleEditScope}
          handleDelete={handleDeleteScope}
          details={details}
          isMobile={isMobile}
          passedStyles={passedStyles}
          showActions={false}
        />
      )}

      {/* Add/Edit Scope Modal */}
      <AddScopeModal
        open={isAddScopeModalOpen}
        onClose={() => {
          setIsAddScopeModalOpen(false);
          setEditingScope(null);
        }}
        onSave={handleScopeSave}
        editingScope={editingScope}
        projectId={details?.id}
      />

      {/* Delete Scope Modal */}
      <DeleteScopeModal
        open={isDeleteScopeModalOpen}
        onClose={() => {
          setIsDeleteScopeModalOpen(false);
          setScopeToDelete(null);
        }}
        scopeData={scopeToDelete}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default Scopes;
